import axiosInstance from "../axios-instance";
import type {
  DSDReportCreatedResponse,
  DSDReportChunkResponse,
  DSDReportListItemResponse,
  DSDReportListResponse,
  DSDReportMetadataResponse,
  DSDReportSubChunkResponse,
  DsdEditorCompanyNameMatchedBlock,
  DsdEditorCompanyNameOccurrence,
  DsdEditorCompanyNameReference,
  DsdEditorMappingReview,
  DsdEditorMappingReviewJudgement,
  DsdEditorMappingReviewTarget,
  DsdEditorNoteReference,
  DsdEditorNoteReferenceCheck,
  DsdEditorMatchedParagraph,
  DsdEditorParagraphSegment,
  DsdEditorReportResponse,
  DsdEditorSpellcheck,
  DsdEditorSpellcheckBlock,
  DsdEditorSpellcheckChange,
  DsdEditorSpellcheckRange,
  DsdEditorValueLink,
  DsdEditorValueMatch,
  DsdEditorValueMatchCandidate,
  DsdEditorValueOccurrence,
  UploadDsdEditorResponse,
} from "./types";

export type {
  DSDReportListItemResponse,
  DSDReportListResponse,
  DsdEditorChunk,
  DsdEditorCompanyNameMatchedBlock,
  DsdEditorCompanyNameOccurrence,
  DsdEditorCompanyNameReference,
  DsdEditorMappingReview,
  DsdEditorMappingReviewJudgement,
  DsdEditorMappingReviewTarget,
  DsdEditorNoteReferenceCheck,
  DsdEditorMatchedParagraph,
  DsdEditorParagraphSegment,
  DsdEditorReportResponse,
  DsdEditorSpellcheck,
  DsdEditorSpellcheckBlock,
  DsdEditorSpellcheckRange,
  DsdEditorValueLink,
  DsdEditorValueMatch,
  DsdEditorValueOccurrence,
  UploadDsdEditorResponse,
} from "./types";

export const uploadDsdEditorFile = async (
  file: File,
): Promise<UploadDsdEditorResponse> => {
  const formData = new FormData();
  formData.append("dsd_file", file);

  const response = await axiosInstance.post("/v1/dsd-editor/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return normalizeDsdEditorCreatedResponse(response.data);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object";

const getString = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value;
  }

  return "";
};

const getNullableString = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (value === null) return null;
    if (typeof value === "string") return value;
  }

  return null;
};

const getNumber = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }

  return 0;
};

const getOptionalNumber = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }

  return undefined;
};

const getArray = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) return value;
  }

  return [];
};

const getStringArray = (record: Record<string, unknown>, keys: string[]) => {
  const values = getArray(record, keys).filter(
    (value): value is string => typeof value === "string" && !!value,
  );

  return values.length ? values : undefined;
};

const getOptionalNumberOrNull = (
  record: Record<string, unknown>,
  keys: string[],
) => {
  for (const key of keys) {
    const value = record[key];
    if (value === null) return null;
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }

  return undefined;
};

const normalizeDsdEditorCreatedResponse = (
  data: unknown,
): DSDReportCreatedResponse => {
  const created = isRecord(data) ? data : {};

  return {
    reportId: getString(created, ["reportId", "report_id"]),
  };
};

const normalizeReportListItem = (value: unknown): DSDReportListItemResponse => {
  const report = isRecord(value) ? value : {};

  return {
    reportId: getString(report, ["reportId", "report_id"]),
    filename: getString(report, ["filename"]),
    uploaderEmail: getString(report, ["uploaderEmail", "uploader_email"]),
    uploadedAt: getString(report, ["uploadedAt", "uploaded_at"]),
    year: getString(report, ["year"]),
    period: getString(report, ["period"]),
    reportType: getString(report, ["reportType", "report_type"]),
  };
};

const normalizeReportList = (data: unknown): DSDReportListResponse => {
  const list = isRecord(data) ? data : {};

  return {
    reports: getArray(list, ["reports"]).map(normalizeReportListItem),
  };
};

export const getDsdEditorReports = async ({
  limit = 20,
  offset = 0,
}: {
  limit?: number;
  offset?: number;
} = {}): Promise<DSDReportListResponse> => {
  const response = await axiosInstance.get("/v1/dsd-editor/reports", {
    params: { limit, offset },
  });

  return normalizeReportList(response.data);
};

const normalizeMetadata = (value: unknown): DSDReportMetadataResponse => {
  const metadata = isRecord(value) ? value : {};

  return {
    companyName: getNullableString(metadata, ["companyName", "company_name"]),
    companyCode: getNullableString(metadata, ["companyCode", "company_code"]),
    reportBase: getString(metadata, ["reportBase", "report_base"]),
    year: getNumber(metadata, ["year"]),
    quarter: getString(metadata, ["quarter"]),
  };
};

const normalizeSubChunk = (value: unknown): DSDReportSubChunkResponse => {
  const subChunk = isRecord(value) ? value : {};

  return {
    decimalNumber: getNumber(subChunk, ["decimalNumber", "decimal_number"]),
    html: getString(subChunk, ["html", "reportHtml", "report_html"]),
  };
};

const normalizeChunk = (value: unknown): DSDReportChunkResponse => {
  const chunk = isRecord(value) ? value : {};

  return {
    id: getString(chunk, ["id"]),
    title: getString(chunk, ["title"]),
    subChunks: getArray(chunk, ["subChunks", "sub_chunks"]).map(
      normalizeSubChunk,
    ),
  };
};

const normalizeOccurrence = (value: unknown): DsdEditorValueOccurrence => {
  const occurrence = isRecord(value) ? value : {};

  return {
    id: getString(occurrence, ["id"]),
    nodeId: getString(occurrence, ["nodeId", "node_id"]),
    chunkId: getString(occurrence, ["chunkId", "chunk_id"]),
    chunkTitle:
      getString(occurrence, ["chunkTitle", "chunk_title"]) || undefined,
    rowLabel: getString(occurrence, ["rowLabel", "row_label"]) || undefined,
    rowLabelPath: getStringArray(occurrence, [
      "rowLabelPath",
      "row_label_path",
    ]),
    columnLabelPath: getStringArray(occurrence, [
      "columnLabelPath",
      "column_label_path",
    ]),
    value: getString(occurrence, ["value"]),
    normalizedValue:
      getString(occurrence, ["normalizedValue", "normalized_value"]) ||
      undefined,
    blockType: getString(occurrence, ["blockType", "block_type"]),
    valueType: getString(occurrence, ["valueType", "value_type"]) || undefined,
  };
};

const normalizeValueMatchCandidate = (
  value: unknown,
): DsdEditorValueMatchCandidate => {
  const candidate = isRecord(value) ? value : {};

  return {
    occurrence: normalizeOccurrence(candidate.occurrence),
    strategies: getArray(candidate, ["strategies"]).filter(
      (strategy): strategy is string => typeof strategy === "string",
    ),
  };
};

const normalizeValueMatch = (value: unknown): DsdEditorValueMatch => {
  const match = isRecord(value) ? value : {};

  return {
    source: normalizeOccurrence(match.source),
    candidates: getArray(match, ["candidates"]).map(
      normalizeValueMatchCandidate,
    ),
  };
};

const normalizeNoteReference = (value: unknown): DsdEditorNoteReference => {
  const reference = isRecord(value) ? value : {};

  return {
    noteNumber: getString(reference, ["noteNumber", "note_number"]),
    chunkId: getNullableString(reference, ["chunkId", "chunk_id"]),
    chunkTitle: getNullableString(reference, ["chunkTitle", "chunk_title"]),
    resolved: reference.resolved === true,
  };
};

const MAPPING_REVIEW_JUDGEMENTS = new Set<string>([
  "no_issue",
  "needs_follow_up",
] satisfies DsdEditorMappingReviewJudgement[]);

const normalizeMappingReviewJudgement = (
  value: unknown,
): DsdEditorMappingReviewJudgement => {
  if (typeof value === "string" && MAPPING_REVIEW_JUDGEMENTS.has(value)) {
    return value as DsdEditorMappingReviewJudgement;
  }
  // 프론트가 모르는 판단 값은 계약 위반 — 폴백하면 틀린 화면이 조용히 나간다
  throw new Error(`알 수 없는 매핑 검토 judgement: ${String(value)}`);
};

const normalizeMappingReview = (value: unknown): DsdEditorMappingReview => {
  const review = isRecord(value) ? value : {};

  return {
    id: getString(review, ["id"]),
    relation: getString(review, ["relation"]),
    coordinatesKey: getString(review, ["coordinatesKey", "coordinates_key"]),
    coordinateVersion: getNumber(review, [
      "coordinateVersion",
      "coordinate_version",
    ]),
    coordinates: getStringArray(review, ["coordinates"]) ?? [],
    judgement: normalizeMappingReviewJudgement(review.judgement),
    reason: getString(review, ["reason"]),
    reviewerId: getString(review, ["reviewerId", "reviewer_id"]),
    judgedAt: getString(review, ["judgedAt", "judged_at"]),
    inheritedFromFilename: getNullableString(review, [
      "inheritedFromFilename",
      "inherited_from_filename",
    ]),
  };
};

const normalizeMappingReviewTarget = (
  value: unknown,
): DsdEditorMappingReviewTarget => {
  const target = isRecord(value) ? value : {};

  return {
    target: getString(target, ["target"]),
    relation: getString(target, ["relation"]),
    coordinates: getStringArray(target, ["coordinates"]) ?? [],
    review: isRecord(target.review)
      ? normalizeMappingReview(target.review)
      : null,
  };
};

const normalizeValueLink = (value: unknown): DsdEditorValueLink => {
  const link = isRecord(value) ? value : {};

  return {
    cellId: getString(link, ["cellId", "cell_id"]),
    value: getString(link, ["value"]),
    noteChunkId: getString(link, ["noteChunkId", "note_chunk_id"]),
    noteTitle: getString(link, ["noteTitle", "note_title"]),
    textMatched: (link.textMatched ?? link.text_matched) === true,
    valueMatched: (link.valueMatched ?? link.value_matched) === true,
    referenced: link.referenced === true,
    hardRule: getNullableString(link, ["hardRule", "hard_rule"]),
    rowLabelPath:
      getStringArray(link, ["rowLabelPath", "row_label_path"]) ?? [],
    columnLabelPath:
      getStringArray(link, ["columnLabelPath", "column_label_path"]) ?? [],
  };
};

const normalizeNoteReferenceCheck = (
  value: unknown,
): DsdEditorNoteReferenceCheck => {
  const check = isRecord(value) ? value : {};

  return {
    source: normalizeOccurrence(check.source),
    noteReferences: getArray(check, ["noteReferences", "note_references"]).map(
      normalizeNoteReference,
    ),
    reviewTargets: getArray(check, ["reviewTargets", "review_targets"]).map(
      normalizeMappingReviewTarget,
    ),
    valueLinks: getArray(check, ["valueLinks", "value_links"]).map(
      normalizeValueLink,
    ),
    unlinkedReferences: getArray(check, [
      "unlinkedReferences",
      "unlinked_references",
    ]).map(normalizeNoteReference),
  };
};

const normalizeSpellcheckRange = (value: unknown): DsdEditorSpellcheckRange => {
  const range = isRecord(value) ? value : {};

  return {
    offset: getNumber(range, ["offset"]),
    length: getNumber(range, ["length"]),
  };
};

const normalizeSpellcheckChange = (
  value: unknown,
): DsdEditorSpellcheckChange => {
  const change = isRecord(value) ? value : {};

  return {
    changeType: getString(change, ["changeType", "change_type"]),
    originalRange: normalizeSpellcheckRange(
      change.originalRange ?? change.original_range,
    ),
    correctedRange: normalizeSpellcheckRange(
      change.correctedRange ?? change.corrected_range,
    ),
    originalRuns: getArray(change, ["originalRuns", "original_runs"]).map(
      normalizeSpellcheckRange,
    ),
    correctedRuns: getArray(change, ["correctedRuns", "corrected_runs"]).map(
      normalizeSpellcheckRange,
    ),
  };
};

const normalizeSpellcheckBlock = (value: unknown): DsdEditorSpellcheckBlock => {
  const block = isRecord(value) ? value : {};

  return {
    blockId: getString(block, ["blockId", "block_id"]),
    chunkId: getString(block, ["chunkId", "chunk_id"]),
    subChunkIndex: getNumber(block, ["subChunkIndex", "sub_chunk_index"]),
    paragraphGroupIndex: getNumber(block, [
      "paragraphGroupIndex",
      "paragraph_group_index",
    ]),
    paragraphIndex: getNumber(block, ["paragraphIndex", "paragraph_index"]),
    tableIndex: getOptionalNumberOrNull(block, ["tableIndex", "table_index"]),
    row: getOptionalNumberOrNull(block, ["row"]),
    col: getOptionalNumberOrNull(block, ["col"]),
    originalText: getString(block, ["originalText", "original_text"]),
    correctedText: getString(block, ["correctedText", "corrected_text"]),
    reason: getString(block, ["reason"]),
    changes: getArray(block, ["changes"]).map(normalizeSpellcheckChange),
  };
};

const normalizeSpellcheck = (value: unknown): DsdEditorSpellcheck => {
  const spellcheck = isRecord(value) ? value : {};

  return {
    blocks: getArray(spellcheck, ["blocks"]).map(normalizeSpellcheckBlock),
  };
};

const normalizeParagraphSegment = (
  value: unknown,
): DsdEditorParagraphSegment => {
  const segment = isRecord(value) ? value : {};
  const occurrenceId = getNullableString(segment, [
    "occurrenceId",
    "occurrence_id",
  ]);

  return {
    type: getString(segment, ["type"]),
    text: getString(segment, ["text"]),
    occurrenceId: occurrenceId || undefined,
  };
};

const normalizeMatchedParagraph = (
  value: unknown,
): DsdEditorMatchedParagraph => {
  const paragraph = isRecord(value) ? value : {};

  return {
    chunkId: getString(paragraph, ["chunkId", "chunk_id"]),
    subChunkIndex: getNumber(paragraph, ["subChunkIndex", "sub_chunk_index"]),
    paragraphGroupIndex: getNumber(paragraph, [
      "paragraphGroupIndex",
      "paragraph_group_index",
    ]),
    paragraphIndex: getNumber(paragraph, ["paragraphIndex", "paragraph_index"]),
    segments: getArray(paragraph, ["segments"]).map(normalizeParagraphSegment),
  };
};

const normalizeCompanyNameOccurrence = (
  value: unknown,
): DsdEditorCompanyNameOccurrence => {
  const occurrence = isRecord(value) ? value : {};

  return {
    occurrenceId: getString(occurrence, ["occurrenceId", "occurrence_id"]),
    surface: getString(occurrence, ["surface"]),
    needsUnification:
      occurrence.needsUnification === true ||
      occurrence.needs_unification === true,
    valueItemRef: getString(occurrence, ["valueItemRef", "value_item_ref"]),
  };
};

const normalizeCompanyNameReference = (
  value: unknown,
): DsdEditorCompanyNameReference => {
  const reference = isRecord(value) ? value : {};

  return {
    representative: getString(reference, ["representative"]),
    occurrences: getArray(reference, ["occurrences"]).map(
      normalizeCompanyNameOccurrence,
    ),
  };
};

const normalizeCompanyNameMatchedBlock = (
  value: unknown,
): DsdEditorCompanyNameMatchedBlock => {
  const block = isRecord(value) ? value : {};

  return {
    valueItemRef: getString(block, ["valueItemRef", "value_item_ref"]),
    blockType: getString(block, ["blockType", "block_type"]),
    chunkId: getString(block, ["chunkId", "chunk_id"]),
    subChunkIndex: getNumber(block, ["subChunkIndex", "sub_chunk_index"]),
    paragraphGroupIndex: getNumber(block, [
      "paragraphGroupIndex",
      "paragraph_group_index",
    ]),
    paragraphIndex: getNumber(block, ["paragraphIndex", "paragraph_index"]),
    segments: getArray(block, ["segments"]).map(normalizeParagraphSegment),
  };
};

const normalizeDsdEditorReportResponse = (
  data: unknown,
): DsdEditorReportResponse => {
  const report = isRecord(data) ? data : {};
  const documentId = getString(report, ["documentId", "document_id"]);
  const contentsHash = getString(report, ["contentsHash", "contents_hash"]);

  const response: DsdEditorReportResponse = {
    reportId: getString(report, ["reportId", "report_id"]),
    filename: getString(report, ["filename"]),
    metadata: normalizeMetadata(report.metadata),
    chunks: getArray(report, ["chunks"]).map(normalizeChunk),
    documentId: documentId || undefined,
    contentsHash: contentsHash || undefined,
    nodeCount: getOptionalNumber(report, ["nodeCount", "node_count"]),
    paragraphCount: getOptionalNumber(report, [
      "paragraphCount",
      "paragraph_count",
    ]),
    tableCellCount: getOptionalNumber(report, [
      "tableCellCount",
      "table_cell_count",
    ]),
    valueMatches: getArray(report, ["valueMatches", "value_matches"]).map(
      normalizeValueMatch,
    ),
    noteValueMatches: getArray(report, [
      "noteValueMatches",
      "note_value_matches",
    ]).map(normalizeValueMatch),
    noteReferenceChecks: getArray(report, [
      "noteReferenceChecks",
      "note_reference_checks",
    ]).map(normalizeNoteReferenceCheck),
    matchedParagraphs: getArray(report, [
      "matchedParagraphs",
      "matched_paragraphs",
    ]).map(normalizeMatchedParagraph),
    companyNameReferences: getArray(report, [
      "companyNameReferences",
      "company_name_references",
    ]).map(normalizeCompanyNameReference),
    companyNameMatchedBlocks: getArray(report, [
      "companyNameMatchedBlocks",
      "company_name_matched_blocks",
    ]).map(normalizeCompanyNameMatchedBlock),
  };

  const sections = getArray(report, ["sections"]).filter(
    (section): section is string => typeof section === "string",
  );

  if (sections.length > 0) {
    response.sections = sections;
  }

  return response;
};

export const getDsdEditorReport = async (
  reportId: string,
  unitDifferenceV2 = false,
): Promise<DsdEditorReportResponse> => {
  // 켜진 분석만 쿼리에 싣는다 (매핑·기업명 분석은 백엔드가 기본으로 내려준다)
  const response = await axiosInstance.get(
    `/v1/dsd-editor/report-preview/${encodeURIComponent(reportId)}`,
    unitDifferenceV2 ? { params: { unit_difference_v2: true } } : undefined,
  );

  return normalizeDsdEditorReportResponse(response.data);
};

const mappingReviewsUrl = (reportId: string) =>
  `/v1/dsd-editor/report-preview/${encodeURIComponent(reportId)}/mapping-reviews`;

// 문서에 유효한 판단 목록. preview 전체를 받지 않고 판단만 필요할 때 쓴다.
export const getDsdEditorMappingReviews = async (
  reportId: string,
): Promise<{ reviews: DsdEditorMappingReview[] }> => {
  const response = await axiosInstance.get(mappingReviewsUrl(reportId));
  const list = isRecord(response.data) ? response.data : {};

  return { reviews: getArray(list, ["reviews"]).map(normalizeMappingReview) };
};

export interface SaveDsdEditorMappingReviewPayload {
  relation: string;
  coordinates: string[];
  judgement: DsdEditorMappingReviewJudgement;
  /** 2,000자 이하 */
  reason?: string;
}

// 주석 참조 검토 판단 기록. 판단 변경도 같은 POST — 서버가 기존 기록을 무효화하고 새로 쌓는다.
// 같은 판단·사유 재전송은 변화 없이 200. 같은 대상에 동시 판단이 들어오면 409.
export const saveDsdEditorMappingReview = async (
  reportId: string,
  payload: SaveDsdEditorMappingReviewPayload,
): Promise<DsdEditorMappingReview> => {
  const response = await axiosInstance.post(
    mappingReviewsUrl(reportId),
    payload,
  );

  return normalizeMappingReview(response.data);
};

// 다른 보고서의 판단을 reportId 로 가져온다. 값이 그대로인 쌍만 판단이 되고, 이미 이력이 있는 쌍은 건드리지 않는다.
// 응답 본문 없음(204) — 몇 건이 넘어왔는지 알 수 없다. 같은 회사·연도·기간·연결/별도 보고서가 아니면 422.
export const inheritDsdEditorMappingReviews = async (
  reportId: string,
  sourceReportId: string,
): Promise<void> => {
  await axiosInstance.post(`${mappingReviewsUrl(reportId)}/inherit`, {
    sourceReportId,
  });
};

// 판단 해제. 편집 권한이 있으면 남의 판단도 해제 가능. 이미 해제된 id도 204.
export const deleteDsdEditorMappingReview = async (
  reportId: string,
  reviewId: string,
): Promise<void> => {
  await axiosInstance.delete(
    `${mappingReviewsUrl(reportId)}/${encodeURIComponent(reviewId)}`,
  );
};

export const getDsdEditorSpellcheck = async (
  reportId: string,
): Promise<DsdEditorSpellcheck> => {
  const response = await axiosInstance.get(
    `/v1/dsd-editor/report-preview/${encodeURIComponent(reportId)}/spellcheck`,
  );

  return normalizeSpellcheck(response.data);
};
